"use strict";
/**
 * Knowledge base for the Fair Work Ombudsman Medical Practitioners Award
 * (MA000031), pay guide effective 1 July 2026 (published 24 June 2026).
 *
 * All rates and allowances in this file are encoded verbatim from the
 * published Fair Work Ombudsman pay guide. Do not edit values without
 * cross-checking the source document.
 *
 * This module exports the data tables (`MEDICAL_PRACTITIONERS_AWARD_2026`)
 * plus a small set of lookup helpers. The legacy
 * `packages/shared/src/award-rates.ts` map is unaffected; that map is
 * what the audit engine currently consumes.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.MEDICAL_PRACTITIONERS_AWARD_2026 = void 0;
exports.getPayRateRow = getPayRateRow;
exports.getRate = getRate;
exports.getOnCallAllowance = getOnCallAllowance;
exports.getStreamFor = getStreamFor;
exports.listClassifications = listClassifications;
exports.listAllClassifications = listAllClassifications;
// ---------------------------------------------------------------------------
// Stream membership
// ---------------------------------------------------------------------------
/** Ordered classifications for the doctor-in-training stream. */
const DOCTOR_IN_TRAINING_CLASSIFICATIONS = [
    'intern',
    'resident-medical-practitioner-pp1',
    'resident-medical-practitioner-pp2',
    'resident-medical-practitioner-pp3',
    'registrar-pp1',
    'registrar-pp2',
    'registrar-pp3',
    'registrar-pp4',
    'senior-registrar-pp1',
    'senior-registrar-pp2',
];
/** Ordered classifications for the career medical practitioner stream. */
const CAREER_MP_CLASSIFICATIONS = [
    'career-medical-practitioner-pp1',
    'career-medical-practitioner-pp2',
    'career-medical-practitioner-pp3',
    'career-medical-practitioner-pp4',
    'senior-career-medical-practitioner-pp1',
    'senior-career-medical-practitioner-pp2',
    'senior-career-medical-practitioner-pp3',
    'senior-career-medical-practitioner-pp4',
];
/** Ordered classifications for the community medical practitioner stream. */
const COMMUNITY_MP_CLASSIFICATIONS = [
    'community-medical-practitioner-pp1',
    'community-medical-practitioner-pp2',
    'community-medical-practitioner-pp3',
    'community-medical-practitioner-pp4',
    'community-medical-practitioner-pp5',
    'community-medical-practitioner-pp6',
    'community-medical-practitioner-pp7',
    'community-medical-practitioner-pp8',
];
/** Ordered classifications for the specialist stream. */
const SPECIALIST_CLASSIFICATIONS = [
    'specialist',
    'senior-specialist-pp1',
    'senior-specialist-pp2',
    'senior-specialist-pp3',
    'senior-specialist-pp4',
    'senior-specialist-pp5',
    'principal-specialist',
    'senior-principal-specialist',
];
/** Ordered classifications for the director-of-medical-services stream. */
const DIRECTOR_MS_CLASSIFICATIONS = [
    'deputy-director-medical-services-pp1',
    'deputy-director-medical-services-pp2',
    'deputy-director-medical-services-pp3',
    'deputy-director-medical-services-pp4',
    'director-medical-services-pp1',
    'director-medical-services-pp2',
    'director-medical-services-pp3',
    'director-medical-services-pp4',
];
const DOCTOR_IN_TRAINING_FT = {
    'intern': [1277.54, 33.62, 50.43, 84.05, 50.43, 67.24, 67.24, 50.43, 67.24, 67.24, 33.62, 31.94],
    'resident-medical-practitioner-pp1': [1356.33, 35.69, 53.54, 89.23, 53.54, 71.38, 71.38, 53.54, 71.38, 71.38, 35.69, 33.91],
    'resident-medical-practitioner-pp2': [1410.96, 37.13, 55.70, 92.83, 55.70, 74.26, 74.26, 55.70, 74.26, 74.26, 37.13, 33.91],
    'resident-medical-practitioner-pp3': [1424.67, 37.49, 56.24, 93.73, 56.24, 74.98, 74.98, 56.24, 74.98, 74.98, 37.49, 33.91],
    'registrar-pp1': [1543.23, 40.61, 60.92, 101.53, 60.92, 81.22, 81.22, 60.92, 81.22, 81.22, 40.61, 38.58],
    'registrar-pp2': [1606.52, 42.28, 63.42, 105.70, 63.42, 84.56, 84.56, 63.42, 84.56, 84.56, 42.28, 38.58],
    'registrar-pp3': [1679.79, 44.21, 66.32, 110.53, 66.32, 88.42, 88.42, 66.32, 88.42, 88.42, 44.21, 38.58],
    'registrar-pp4': [1731.12, 45.56, 68.34, 113.90, 68.34, 91.12, 91.12, 68.34, 91.12, 91.12, 45.56, 38.58],
    'senior-registrar-pp1': [2016.81, 53.07, 79.61, 132.68, 79.61, 106.14, 106.14, 79.61, 106.14, 106.14, 53.07, 50.42],
    'senior-registrar-pp2': [2096.35, 55.17, 82.76, 137.93, 82.76, 110.34, 110.34, 82.76, 110.34, 110.34, 55.17, 50.42],
};
const DOCTOR_IN_TRAINING_CASUAL = {
    'intern': [42.03, 58.84, 92.46, 58.84, 75.65, 75.65, 50.43, 67.24, 67.24, 42.03, 31.94],
    'resident-medical-practitioner-pp1': [44.61, 62.46, 98.15, 62.46, 80.30, 80.30, 53.54, 71.38, 71.38, 44.61, 33.91],
    'resident-medical-practitioner-pp2': [46.41, 64.98, 102.11, 64.98, 83.54, 83.54, 55.70, 74.26, 74.26, 46.41, 33.91],
    'resident-medical-practitioner-pp3': [46.86, 65.61, 103.10, 65.61, 84.35, 84.35, 56.24, 74.98, 74.98, 46.86, 33.91],
    'registrar-pp1': [50.76, 71.07, 111.68, 71.07, 91.37, 91.37, 60.92, 81.22, 81.22, 50.76, 38.58],
    'registrar-pp2': [52.85, 73.99, 116.27, 73.99, 95.13, 95.13, 63.42, 84.56, 84.56, 52.85, 38.58],
    'registrar-pp3': [55.26, 77.37, 121.58, 77.37, 99.47, 99.47, 66.32, 88.42, 88.42, 55.26, 38.58],
    'registrar-pp4': [56.95, 79.73, 125.29, 79.73, 102.51, 102.51, 68.34, 91.12, 91.12, 56.95, 38.58],
    'senior-registrar-pp1': [66.34, 92.87, 145.94, 92.87, 119.41, 119.41, 79.61, 106.14, 106.14, 66.34, 50.42],
    'senior-registrar-pp2': [68.96, 96.55, 151.72, 96.55, 124.13, 124.13, 82.76, 110.34, 110.34, 68.96, 50.42],
};
const CAREER_MP_FT = {
    'career-medical-practitioner-pp1': [2037.52, 53.62, 80.43, 134.05, 80.43, 107.24, 107.24, 80.43, 107.24, 107.24, 60.32, 67.03, 80.43, 93.84],
    'career-medical-practitioner-pp2': [2113.15, 55.61, 83.42, 139.03, 83.42, 111.22, 111.22, 83.42, 111.22, 111.22, 62.56, 69.51, 83.42, 97.32],
    'career-medical-practitioner-pp3': [2155.08, 56.71, 85.07, 141.78, 85.07, 113.42, 113.42, 85.07, 113.42, 113.42, 63.80, 70.89, 85.07, 99.24],
    'career-medical-practitioner-pp4': [2234.38, 58.80, 88.20, 147.00, 88.20, 117.60, 117.60, 88.20, 117.60, 117.60, 66.15, 73.50, 88.20, 102.90],
    'senior-career-medical-practitioner-pp1': [2304.85, 60.65, 90.98, 151.63, 90.98, 121.30, 121.30, 90.98, 121.30, 121.30, 68.23, 75.81, 90.98, 106.14],
    'senior-career-medical-practitioner-pp2': [2378.17, 62.58, 93.87, 156.45, 93.87, 125.16, 125.16, 93.87, 125.16, 125.16, 70.40, 78.23, 93.87, 109.52],
    'senior-career-medical-practitioner-pp3': [2458.04, 64.69, 97.04, 161.73, 97.04, 129.38, 129.38, 97.04, 129.38, 129.38, 72.78, 80.86, 97.04, 113.21],
    'senior-career-medical-practitioner-pp4': [2532.77, 66.65, 99.98, 166.63, 99.98, 133.30, 133.30, 99.98, 133.30, 133.30, 74.98, 83.31, 99.98, 116.64],
};
const CAREER_MP_CASUAL = {
    'career-medical-practitioner-pp1': [67.03, 93.84, 147.46, 93.84, 120.65, 120.65, 80.43, 107.24, 107.24, 73.73, 80.43, 93.84, 107.24],
    'career-medical-practitioner-pp2': [69.51, 97.32, 152.93, 97.32, 125.12, 125.12, 83.42, 111.22, 111.22, 76.46, 83.42, 97.32, 111.22],
    'career-medical-practitioner-pp3': [70.89, 99.24, 155.95, 99.24, 127.60, 127.60, 85.07, 113.42, 113.42, 77.98, 85.07, 99.24, 113.42],
    'career-medical-practitioner-pp4': [73.50, 102.90, 161.70, 102.90, 132.30, 132.30, 88.20, 117.60, 117.60, 80.85, 88.20, 102.90, 117.60],
    'senior-career-medical-practitioner-pp1': [75.81, 106.14, 166.79, 106.14, 136.46, 136.46, 90.98, 121.30, 121.30, 83.39, 90.98, 106.14, 121.30],
    'senior-career-medical-practitioner-pp2': [78.23, 109.52, 172.10, 109.52, 140.81, 140.81, 93.87, 125.16, 125.16, 86.05, 93.87, 109.52, 125.16],
    'senior-career-medical-practitioner-pp3': [80.86, 113.21, 177.90, 113.21, 145.55, 145.55, 97.04, 129.38, 129.38, 88.95, 97.04, 113.21, 129.38],
    'senior-career-medical-practitioner-pp4': [83.31, 116.64, 183.29, 116.64, 149.96, 149.96, 99.98, 133.30, 133.30, 91.64, 99.98, 116.64, 133.30],
};
const COMMUNITY_MP_FT = {
    'community-medical-practitioner-pp1': [2037.15, 53.61, 80.42, 134.03, 80.42, 107.22, 107.22, 80.42, 107.22, 107.22, 54.95, 55.75, 56.29],
    'community-medical-practitioner-pp2': [2111.60, 55.57, 83.36, 138.93, 83.36, 111.14, 111.14, 83.36, 111.14, 111.14, 56.96, 57.79, 58.35],
    'community-medical-practitioner-pp3': [2179.77, 57.36, 86.04, 143.40, 86.04, 114.72, 114.72, 86.04, 114.72, 114.72, 58.79, 59.65, 60.23],
    'community-medical-practitioner-pp4': [2234.29, 58.80, 88.20, 147.00, 88.20, 117.60, 117.60, 88.20, 117.60, 117.60, 60.27, 61.15, 61.74],
    'community-medical-practitioner-pp5': [2304.48, 60.64, 90.96, 151.60, 90.96, 121.28, 121.28, 90.96, 121.28, 121.28, 62.16, 63.07, 63.67],
    'community-medical-practitioner-pp6': [2377.25, 62.56, 93.84, 156.40, 93.84, 125.12, 125.12, 93.84, 125.12, 125.12, 64.12, 65.06, 65.69],
    'community-medical-practitioner-pp7': [2456.77, 64.65, 96.98, 161.63, 96.98, 129.30, 129.30, 96.98, 129.30, 129.30, 66.27, 67.24, 67.88],
    'community-medical-practitioner-pp8': [2531.21, 66.61, 99.92, 166.53, 99.92, 133.22, 133.22, 99.92, 133.22, 133.22, 68.28, 69.27, 69.94],
};
const COMMUNITY_MP_CASUAL = {
    'community-medical-practitioner-pp1': [67.01, 93.82, 147.43, 93.82, 120.62, 120.62, 80.42, 107.22, 107.22, 68.35, 69.16, 69.69],
    'community-medical-practitioner-pp2': [69.46, 97.25, 152.82, 97.25, 125.03, 125.03, 83.36, 111.14, 111.14, 70.85, 71.69, 72.24],
    'community-medical-practitioner-pp3': [71.70, 100.38, 157.74, 100.38, 129.06, 129.06, 86.04, 114.72, 114.72, 73.13, 73.99, 74.57],
    'community-medical-practitioner-pp4': [73.50, 102.90, 161.70, 102.90, 132.30, 132.30, 88.20, 117.60, 117.60, 74.97, 75.85, 76.44],
    'community-medical-practitioner-pp5': [75.80, 106.12, 166.76, 106.12, 136.44, 136.44, 90.96, 121.28, 121.28, 77.32, 78.23, 78.83],
    'community-medical-practitioner-pp6': [78.20, 109.48, 172.04, 109.48, 140.76, 140.76, 93.84, 125.12, 125.12, 79.76, 80.70, 81.33],
    'community-medical-practitioner-pp7': [80.81, 113.14, 177.79, 113.14, 145.46, 145.46, 96.98, 129.30, 129.30, 82.43, 83.40, 84.05],
    'community-medical-practitioner-pp8': [83.26, 116.57, 183.18, 116.57, 149.87, 149.87, 99.92, 133.22, 133.22, 84.93, 85.93, 86.59],
};
const SPECIALIST_FT = {
    'specialist': [2570.93, 67.66, 101.49, 169.15, 76.12, 101.49, 118.41],
    'senior-specialist-pp1': [2749.01, 72.35, 108.53, 180.88, 81.39, 108.53, 126.61],
    'senior-specialist-pp2': [2843.41, 74.82, 112.23, 187.05, 84.17, 112.23, 130.94],
    'senior-specialist-pp3': [2940.72, 77.39, 116.09, 193.48, 87.06, 116.09, 135.43],
    'senior-specialist-pp4': [3149.16, 82.87, 124.31, 207.18, 93.23, 124.31, 145.02],
    'senior-specialist-pp5': [3193.92, 84.05, 126.08, 210.13, 94.56, 126.08, 147.09],
    'principal-specialist': [3259.00, 85.77, 128.66, 214.43, 96.49, 128.66, 150.10],
    'senior-principal-specialist': [3374.36, 88.80, 133.20, 222.00, 99.90, 133.20, 155.40],
};
const SPECIALIST_CASUAL = {
    'specialist': [84.58, 118.41, 186.07, 93.03, 118.41, 135.32],
    'senior-specialist-pp1': [90.44, 126.61, 198.96, 99.48, 126.61, 144.70],
    'senior-specialist-pp2': [93.53, 130.94, 205.76, 102.88, 130.94, 149.64],
    'senior-specialist-pp3': [96.74, 135.43, 212.82, 106.41, 135.43, 154.78],
    'senior-specialist-pp4': [103.59, 145.02, 227.89, 113.95, 145.02, 165.74],
    'senior-specialist-pp5': [105.06, 147.09, 231.14, 115.57, 147.09, 168.10],
    'principal-specialist': [107.21, 150.10, 235.87, 117.93, 150.10, 171.54],
    'senior-principal-specialist': [111.00, 155.40, 244.20, 122.10, 155.40, 177.60],
};
const DIRECTOR_MS_FT = {
    'deputy-director-medical-services-pp1': [2270.61, 59.75, 89.63, 149.38, 67.22, 89.63, 104.56],
    'deputy-director-medical-services-pp2': [2490.26, 65.54, 98.31, 163.85, 73.73, 98.31, 114.70],
    'deputy-director-medical-services-pp3': [2749.01, 72.35, 108.53, 180.88, 81.39, 108.53, 126.61],
    'deputy-director-medical-services-pp4': [3043.06, 80.08, 120.12, 200.20, 90.09, 120.12, 140.14],
    'director-medical-services-pp1': [2570.32, 67.64, 101.46, 169.10, 76.10, 101.46, 118.37],
    'director-medical-services-pp2': [2842.08, 74.79, 112.19, 186.98, 84.14, 112.19, 130.88],
    'director-medical-services-pp3': [3259.00, 85.77, 128.66, 214.43, 96.49, 128.66, 150.10],
    'director-medical-services-pp4': [3525.18, 92.76, 139.14, 231.90, 104.36, 139.14, 162.33],
};
const DIRECTOR_MS_CASUAL = {
    'deputy-director-medical-services-pp1': [74.69, 104.56, 164.31, 82.16, 104.56, 119.50],
    'deputy-director-medical-services-pp2': [81.93, 114.70, 180.24, 90.12, 114.70, 131.08],
    'deputy-director-medical-services-pp3': [90.44, 126.61, 198.96, 99.48, 126.61, 144.70],
    'deputy-director-medical-services-pp4': [100.10, 140.14, 220.22, 110.11, 140.14, 160.16],
    'director-medical-services-pp1': [84.55, 118.37, 186.01, 93.01, 118.37, 135.28],
    'director-medical-services-pp2': [93.49, 130.88, 205.67, 102.84, 130.88, 149.58],
    'director-medical-services-pp3': [107.21, 150.10, 235.87, 117.93, 150.10, 171.54],
    'director-medical-services-pp4': [115.95, 162.33, 255.09, 127.55, 162.33, 185.52],
};
// ---------------------------------------------------------------------------
// Row builders
// ---------------------------------------------------------------------------
function traineeFtRow(classification) {
    const [weekly, hourly, satSun, ph, otFirst, otAfter, otSun, rMf, rSs, rPh, nightHourly, nightOcc] = DOCTOR_IN_TRAINING_FT[classification];
    return {
        classification,
        stream: 'doctor_in_training',
        employmentType: 'full_time_or_part_time',
        rates: {
            weekly,
            hourly,
            saturday_sunday: satSun,
            public_holiday: ph,
            overtime_mon_sat_first_2h: otFirst,
            overtime_mon_sat_after_2h: otAfter,
            overtime_sunday: otSun,
            recall_mon_fri: rMf,
            recall_sat_sun: rSs,
            recall_public_holiday: rPh,
            shift_9pm_to_6am_hourly: nightHourly,
            shift_9pm_to_6am_per_occasion: nightOcc,
        },
    };
}
function traineeCasualRow(classification) {
    const [hourly, satSun, ph, otFirst, otAfter, otSun, rMf, rSs, rPh, nightHourly, nightOcc] = DOCTOR_IN_TRAINING_CASUAL[classification];
    return {
        classification,
        stream: 'doctor_in_training',
        employmentType: 'casual',
        rates: {
            hourly,
            saturday_sunday: satSun,
            public_holiday: ph,
            overtime_mon_sat_first_2h: otFirst,
            overtime_mon_sat_after_2h: otAfter,
            overtime_sunday: otSun,
            recall_mon_fri: rMf,
            recall_sat_sun: rSs,
            recall_public_holiday: rPh,
            shift_9pm_to_6am_hourly: nightHourly,
            shift_9pm_to_6am_per_occasion: nightOcc,
        },
    };
}
function careerFtRow(classification) {
    const [weekly, hourly, satSun, ph, otFirst, otAfter, otSun, rMf, rSs, rPh, shiftMfEv, shiftMfOv, shiftSat, shiftSun] = CAREER_MP_FT[classification];
    return {
        classification,
        stream: 'career_medical_practitioner',
        employmentType: 'full_time_or_part_time',
        rates: {
            weekly,
            hourly,
            saturday_sunday: satSun,
            public_holiday: ph,
            overtime_mon_sat_first_2h: otFirst,
            overtime_mon_sat_after_2h: otAfter,
            overtime_sunday: otSun,
            recall_mon_fri: rMf,
            recall_sat_sun: rSs,
            recall_public_holiday: rPh,
            shift_mon_fri_6pm_midnight: shiftMfEv,
            shift_mon_fri_midnight_8am: shiftMfOv,
            shift_saturday: shiftSat,
            shift_sunday: shiftSun,
        },
    };
}
function careerCasualRow(classification) {
    const [hourly, satSun, ph, otFirst, otAfter, otSun, rMf, rSs, rPh, shiftMfEv, shiftMfOv, shiftSat, shiftSun] = CAREER_MP_CASUAL[classification];
    return {
        classification,
        stream: 'career_medical_practitioner',
        employmentType: 'casual',
        rates: {
            hourly,
            saturday_sunday: satSun,
            public_holiday: ph,
            overtime_mon_sat_first_2h: otFirst,
            overtime_mon_sat_after_2h: otAfter,
            overtime_sunday: otSun,
            recall_mon_fri: rMf,
            recall_sat_sun: rSs,
            recall_public_holiday: rPh,
            shift_mon_fri_6pm_midnight: shiftMfEv,
            shift_mon_fri_midnight_8am: shiftMfOv,
            shift_saturday: shiftSat,
            shift_sunday: shiftSun,
        },
    };
}
function communityFtRow(classification) {
    const [weekly, hourly, satSun, ph, otFirst, otAfter, otSun, rMf, rSs, rPh, shiftEdge, shiftMid, shiftMid4w] = COMMUNITY_MP_FT[classification];
    return {
        classification,
        stream: 'community_medical_practitioner',
        employmentType: 'full_time_or_part_time',
        rates: {
            weekly,
            hourly,
            saturday_sunday: satSun,
            public_holiday: ph,
            overtime_mon_sat_first_2h: otFirst,
            overtime_mon_sat_after_2h: otAfter,
            overtime_sunday: otSun,
            recall_mon_fri: rMf,
            recall_sat_sun: rSs,
            recall_public_holiday: rPh,
            shift_starts_5am_630am_or_finishes_6pm_midnight: shiftEdge,
            shift_midnight_5am: shiftMid,
            shift_midnight_5am_4w: shiftMid4w,
        },
    };
}
function communityCasualRow(classification) {
    const [hourly, satSun, ph, otFirst, otAfter, otSun, rMf, rSs, rPh, shiftEdge, shiftMid, shiftMid4w] = COMMUNITY_MP_CASUAL[classification];
    return {
        classification,
        stream: 'community_medical_practitioner',
        employmentType: 'casual',
        rates: {
            hourly,
            saturday_sunday: satSun,
            public_holiday: ph,
            overtime_mon_sat_first_2h: otFirst,
            overtime_mon_sat_after_2h: otAfter,
            overtime_sunday: otSun,
            recall_mon_fri: rMf,
            recall_sat_sun: rSs,
            recall_public_holiday: rPh,
            shift_starts_5am_630am_or_finishes_6pm_midnight: shiftEdge,
            shift_midnight_5am: shiftMid,
            shift_midnight_5am_4w: shiftMid4w,
        },
    };
}
function specialistFtRow(classification) {
    const [weekly, hourly, satSun, ph, shiftMfEv, shiftSat, shiftSun] = SPECIALIST_FT[classification];
    return {
        classification,
        stream: 'specialist',
        employmentType: 'full_time_or_part_time',
        rates: {
            weekly,
            hourly,
            saturday_sunday: satSun,
            public_holiday: ph,
            shift_mon_fri_6pm_midnight: shiftMfEv,
            shift_saturday_7am_midnight: shiftSat,
            shift_sunday_7am_midnight: shiftSun,
        },
    };
}
function specialistCasualRow(classification) {
    const [hourly, satSun, ph, shiftMfEv, shiftSat, shiftSun] = SPECIALIST_CASUAL[classification];
    return {
        classification,
        stream: 'specialist',
        employmentType: 'casual',
        rates: {
            hourly,
            saturday_sunday: satSun,
            public_holiday: ph,
            shift_mon_fri_6pm_midnight: shiftMfEv,
            shift_saturday_7am_midnight: shiftSat,
            shift_sunday_7am_midnight: shiftSun,
        },
    };
}
function directorFtRow(classification) {
    const [weekly, hourly, satSun, ph, shiftMfEv, shiftSat, shiftSun] = DIRECTOR_MS_FT[classification];
    return {
        classification,
        stream: 'director_medical_services',
        employmentType: 'full_time_or_part_time',
        rates: {
            weekly,
            hourly,
            saturday_sunday: satSun,
            public_holiday: ph,
            shift_mon_fri_6pm_midnight: shiftMfEv,
            shift_saturday_7am_midnight: shiftSat,
            shift_sunday_7am_midnight: shiftSun,
        },
    };
}
function directorCasualRow(classification) {
    const [hourly, satSun, ph, shiftMfEv, shiftSat, shiftSun] = DIRECTOR_MS_CASUAL[classification];
    return {
        classification,
        stream: 'director_medical_services',
        employmentType: 'casual',
        rates: {
            hourly,
            saturday_sunday: satSun,
            public_holiday: ph,
            shift_mon_fri_6pm_midnight: shiftMfEv,
            shift_saturday_7am_midnight: shiftSat,
            shift_sunday_7am_midnight: shiftSun,
        },
    };
}
// ---------------------------------------------------------------------------
// Assembled pay rate rows
// ---------------------------------------------------------------------------
const PAY_RATE_ROWS = [
    ...DOCTOR_IN_TRAINING_CLASSIFICATIONS.map(traineeFtRow),
    ...DOCTOR_IN_TRAINING_CLASSIFICATIONS.map(traineeCasualRow),
    ...CAREER_MP_CLASSIFICATIONS.map(careerFtRow),
    ...CAREER_MP_CLASSIFICATIONS.map(careerCasualRow),
    ...COMMUNITY_MP_CLASSIFICATIONS.map(communityFtRow),
    ...COMMUNITY_MP_CLASSIFICATIONS.map(communityCasualRow),
    ...SPECIALIST_CLASSIFICATIONS.map(specialistFtRow),
    ...SPECIALIST_CLASSIFICATIONS.map(specialistCasualRow),
    ...DIRECTOR_MS_CLASSIFICATIONS.map(directorFtRow),
    ...DIRECTOR_MS_CLASSIFICATIONS.map(directorCasualRow),
];
// ---------------------------------------------------------------------------
// Allowances
// ---------------------------------------------------------------------------
/** Per-hour, all-purpose on-call allowance amounts for Senior Doctors. */
const ON_CALL_PER_HOUR = [
    ['specialist', 6.15],
    ['senior-specialist-pp1', 6.58],
    ['senior-specialist-pp2', 6.80],
    ['senior-specialist-pp3', 7.04],
    ['senior-specialist-pp4', 7.53],
    ['senior-specialist-pp5', 7.64],
    ['principal-specialist', 7.80],
    ['senior-principal-specialist', 8.07],
    ['deputy-director-medical-services-pp1', 5.43],
    ['deputy-director-medical-services-pp2', 5.96],
    ['deputy-director-medical-services-pp3', 6.58],
    ['deputy-director-medical-services-pp4', 7.28],
    ['director-medical-services-pp1', 6.15],
    ['director-medical-services-pp2', 6.80],
    ['director-medical-services-pp3', 7.80],
    ['director-medical-services-pp4', 8.43],
];
/** Per-day on-call allowance amounts for non-Senior Doctors. */
const ON_CALL_PER_DAY = [
    ['intern', 25.55],
    ['resident-medical-practitioner-pp1', 27.13],
    ['resident-medical-practitioner-pp2', 28.22],
    ['resident-medical-practitioner-pp3', 28.49],
    ['registrar-pp1', 30.87],
    ['registrar-pp2', 32.13],
    ['registrar-pp3', 33.60],
    ['registrar-pp4', 34.62],
    ['senior-registrar-pp1', 40.34],
    ['senior-registrar-pp2', 41.93],
    ['career-medical-practitioner-pp1', 40.75],
    ['career-medical-practitioner-pp2', 42.26],
    ['career-medical-practitioner-pp3', 43.10],
    ['career-medical-practitioner-pp4', 44.69],
    ['senior-career-medical-practitioner-pp1', 46.10],
    ['senior-career-medical-practitioner-pp2', 47.56],
    ['senior-career-medical-practitioner-pp3', 49.16],
    ['senior-career-medical-practitioner-pp4', 50.66],
    ['community-medical-practitioner-pp1', 40.74],
    ['community-medical-practitioner-pp2', 42.23],
    ['community-medical-practitioner-pp3', 43.60],
    ['community-medical-practitioner-pp4', 44.69],
    ['community-medical-practitioner-pp5', 46.09],
    ['community-medical-practitioner-pp6', 47.55],
    ['community-medical-practitioner-pp7', 49.14],
    ['community-medical-practitioner-pp8', 50.62],
];
const ALLOWANCES = [
    {
        code: 'meal',
        description: 'Meal allowance',
        amountAud: 17.30,
        unit: 'per_occasion',
        allPurpose: false,
        notes: 'Further $17.30 when more than 15 continuous hours are worked',
    },
    ...ON_CALL_PER_HOUR.map(([classification, amount]) => ({
        code: `on-call-${classification}`,
        description: `On-call allowance for ${classification}`,
        amountAud: amount,
        unit: 'per_hour',
        allPurpose: true,
        appliesTo: classification,
        notes: 'All-purpose allowance — already included in the Senior Doctor rate tables',
    })),
    ...ON_CALL_PER_DAY.map(([classification, amount]) => ({
        code: `on-call-${classification}`,
        description: `On-call allowance for ${classification}`,
        amountAud: amount,
        unit: 'per_day',
        allPurpose: false,
        appliesTo: classification,
    })),
    {
        code: 'recall-travel-time',
        description: 'Recall to duty travel time (other than Senior Doctors)',
        amountAud: 0,
        unit: 'reimbursement',
        allPurpose: false,
        notes: "Payment of an amount equal to 1 hour at the minimum hourly rate for the employee's classification",
    },
    {
        code: 'managerial-senior-doctor-level-1',
        description: 'Managerial allowance — Senior Doctor level 1',
        amountAud: 7225.39,
        unit: 'per_annum',
        allPurpose: false,
    },
    {
        code: 'managerial-senior-doctor-level-2',
        description: 'Managerial allowance — Senior Doctor level 2',
        amountAud: 16919.88,
        unit: 'per_annum',
        allPurpose: false,
    },
    {
        code: 'managerial-senior-doctor-level-3',
        description: 'Managerial allowance — Senior Doctor level 3',
        amountAud: 26640.37,
        unit: 'per_annum',
        allPurpose: false,
    },
    {
        code: 'sleepover',
        description: 'Sleepover allowance',
        amountAud: 103.96,
        unit: 'per_period',
        allPurpose: false,
        notes: 'If the employee works more than 1 hour during the sleepover, the appropriate overtime payment will apply',
    },
    {
        code: 'telephone-reimbursement',
        description: 'Telephone reimbursement when required to install/maintain phone for on-call',
        amountAud: 0,
        unit: 'reimbursement',
        allPurpose: false,
        notes: 'Reimbursement for installation and rental charges',
    },
    {
        code: 'travelling-on-duty',
        description: 'Travelling on duty reimbursement',
        amountAud: 0,
        unit: 'reimbursement',
        allPurpose: false,
        notes: 'Reimbursement for reasonably incurred fares, meals, and accommodation on production of receipts',
    },
    {
        code: 'vehicle',
        description: 'Vehicle allowance',
        amountAud: 1.01,
        unit: 'per_km',
        allPurpose: false,
    },
];
// ---------------------------------------------------------------------------
// Knowledge base
// ---------------------------------------------------------------------------
/**
 * The complete knowledge base for the Medical Practitioners Award (MA000031),
 * pay guide effective 1 July 2026.
 */
exports.MEDICAL_PRACTITIONERS_AWARD_2026 = {
    code: 'MA000031',
    title: 'Pay Guide - Medical Practitioners Award',
    effectiveDate: '2026-07-01',
    publishedDate: '2026-06-24',
    source: 'Fair Work Ombudsman — Pay Guide MA000031 (24 June 2026)',
    disclaimer: 'This pay guide is informational. If our information is inconsistent with the award, the award applies. Always confirm with the Fair Work Ombudsman, your union, or your employer before raising a formal claim.',
    payRateRows: PAY_RATE_ROWS,
    allowances: ALLOWANCES,
};
// ---------------------------------------------------------------------------
// Lookup helpers
// ---------------------------------------------------------------------------
/**
 * Returns the pay rate row for a classification at the given employment type,
 * or `undefined` if no row matches.
 */
function getPayRateRow(classification, employmentType) {
    return exports.MEDICAL_PRACTITIONERS_AWARD_2026.payRateRows.find((row) => row.classification === classification && row.employmentType === employmentType);
}
/**
 * Returns the AUD amount for a single rate column on a classification's row,
 * or `undefined` if either the row or the column is not defined.
 */
function getRate(classification, employmentType, column) {
    const row = getPayRateRow(classification, employmentType);
    return row?.rates[column];
}
/**
 * Returns the on-call allowance entry that applies to the given classification,
 * or `undefined` if none is defined.
 *
 * The lookup is deterministic: it selects the first allowance whose `code`
 * starts with `on-call-` and whose `appliesTo` matches the classification.
 */
function getOnCallAllowance(classification) {
    return exports.MEDICAL_PRACTITIONERS_AWARD_2026.allowances.find((a) => a.appliesTo === classification && a.code.startsWith('on-call-'));
}
/**
 * Returns the stream a classification belongs to. Total function: every
 * value of `ClassificationCode` is handled.
 */
function getStreamFor(classification) {
    switch (classification) {
        case 'intern':
        case 'resident-medical-practitioner-pp1':
        case 'resident-medical-practitioner-pp2':
        case 'resident-medical-practitioner-pp3':
        case 'registrar-pp1':
        case 'registrar-pp2':
        case 'registrar-pp3':
        case 'registrar-pp4':
        case 'senior-registrar-pp1':
        case 'senior-registrar-pp2':
            return 'doctor_in_training';
        case 'career-medical-practitioner-pp1':
        case 'career-medical-practitioner-pp2':
        case 'career-medical-practitioner-pp3':
        case 'career-medical-practitioner-pp4':
        case 'senior-career-medical-practitioner-pp1':
        case 'senior-career-medical-practitioner-pp2':
        case 'senior-career-medical-practitioner-pp3':
        case 'senior-career-medical-practitioner-pp4':
            return 'career_medical_practitioner';
        case 'community-medical-practitioner-pp1':
        case 'community-medical-practitioner-pp2':
        case 'community-medical-practitioner-pp3':
        case 'community-medical-practitioner-pp4':
        case 'community-medical-practitioner-pp5':
        case 'community-medical-practitioner-pp6':
        case 'community-medical-practitioner-pp7':
        case 'community-medical-practitioner-pp8':
            return 'community_medical_practitioner';
        case 'specialist':
        case 'senior-specialist-pp1':
        case 'senior-specialist-pp2':
        case 'senior-specialist-pp3':
        case 'senior-specialist-pp4':
        case 'senior-specialist-pp5':
        case 'principal-specialist':
        case 'senior-principal-specialist':
            return 'specialist';
        case 'deputy-director-medical-services-pp1':
        case 'deputy-director-medical-services-pp2':
        case 'deputy-director-medical-services-pp3':
        case 'deputy-director-medical-services-pp4':
        case 'director-medical-services-pp1':
        case 'director-medical-services-pp2':
        case 'director-medical-services-pp3':
        case 'director-medical-services-pp4':
            return 'director_medical_services';
    }
}
/**
 * Returns all classifications belonging to a stream, in pay-point order.
 */
function listClassifications(stream) {
    switch (stream) {
        case 'doctor_in_training':
            return DOCTOR_IN_TRAINING_CLASSIFICATIONS;
        case 'career_medical_practitioner':
            return CAREER_MP_CLASSIFICATIONS;
        case 'community_medical_practitioner':
            return COMMUNITY_MP_CLASSIFICATIONS;
        case 'specialist':
            return SPECIALIST_CLASSIFICATIONS;
        case 'director_medical_services':
            return DIRECTOR_MS_CLASSIFICATIONS;
    }
}
/**
 * Returns every classification code defined by the award, in canonical
 * stream/pay-point order.
 */
function listAllClassifications() {
    return [
        ...DOCTOR_IN_TRAINING_CLASSIFICATIONS,
        ...CAREER_MP_CLASSIFICATIONS,
        ...COMMUNITY_MP_CLASSIFICATIONS,
        ...SPECIALIST_CLASSIFICATIONS,
        ...DIRECTOR_MS_CLASSIFICATIONS,
    ];
}
//# sourceMappingURL=medical-practitioners-ma000031.js.map